import { useState, useEffect, useCallback } from "react";
import CommonTitle from "../ui-components/CommonTitle";
import DatakanriDataMakingLocation from "../ui-components/DatakanriDataMakingLocation";
import { Flex, TextField, Text } from "@aws-amplify/ui-react";
import CommonBtnBlue from "../ui-components/CommonBtnBlue";
import Myicons from "../ui-components/Myicons";
import { DEFAULT_DISPLAY_SETTING } from "../lib/ConstList";
import { API, graphqlOperation } from 'aws-amplify';
import { createTrendGraph } from '../graphql/mutations';
import {
  getDisplaySettingJson,
} from '../lib/SettingUtil';
import { formatDatetime } from "../utils/datetime";

const CreateTrendGraph = () => {
  // 表示ラベル設定
  const [displaySettingJson, setDisplaySettingJson] = useState(DEFAULT_DISPLAY_SETTING);

  // 選択された地点
  const [selectedLocation, setSelectedLocation] = useState("YSCC");

  // 選択されたスロット
  const [selectedSlot, setSelectedSlot] = useState("3400-3440");

  // 選択された期間
  const [selectedPeriod, setSelectedPeriod] = useState({
    start: "",
    end: "",
  });

  // 期間のバリデーションフラグ
  const [validPeriodMeg, setValidPeriodMeg] = useState("");

  // ロード中フラグ
  const [isLoading, setIsLoading] = useState(false);

  // 表示設定取得
  const initDisplaySettingJson = useCallback(async () => {
    const displaySettingJson = await getDisplaySettingJson();
    if (displaySettingJson !== null) {
      setDisplaySettingJson(displaySettingJson);
    } else {
      console.log("displaySettingJson is null");
    }
  }, []);

  // バリデーションチェック
  const validate = useCallback(() => {
    if (selectedPeriod.start === "" || selectedPeriod.end === "") {
      setValidPeriodMeg("期間を選択してください");
      return false;
    }
    if (selectedPeriod.start > selectedPeriod.end) {
      setValidPeriodMeg("開始終了期間が逆転しています");
      return false;
    }

    // 2024-03より前の期間は選択できない
    if (selectedPeriod.start < "2024-03" || selectedPeriod.end < "2024-03") {
      setValidPeriodMeg("2024年3月より前の期間は選択できません");
      return false;
    }

    // 終了期間が今月までしか選択できない
    if (selectedPeriod.end > formatDatetime(new Date(), "YYYY-MM")) {
      setValidPeriodMeg("期間は今月までしか選択できません");
      return false;
    }
    
    setValidPeriodMeg("");
    return true;
  }, [selectedPeriod]);

  // 作成ボタンクリック時の処理
  const onClickCreateBtn = async () => {
    console.log("create tend graph");

    // すでに作成中の場合はエラーを表示
    if (isLoading) {
      return;
    }

    // ロード中フラグを立てる
    setIsLoading(true);

    // バリデーションチェック
    if (!validate()) {
      console.log("validate error");
      
      // ロード中フラグを下げる
      setIsLoading(false);
      return;
    }
    
    try {
      // 推移グラフ作成リクエスト送信
      const response = await API.graphql(graphqlOperation(createTrendGraph, {
        input: {
          type: "trendGraph",
          location_name: selectedLocation,
          band: selectedSlot,
          status: "pending",
          start_at: selectedPeriod.start,
          end_at: selectedPeriod.end,
        }
      }));

      console.log("createTrendGraph response: ", response);
    } catch (error) {
      console.error("createTrendGraph error: ", error);
    }

    // 1秒後にロード中フラグを下げる
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }

  useEffect(() => {
    // 表示設定取得
    initDisplaySettingJson();
  }, [initDisplaySettingJson]);

  return (
    <>
      <Flex
        gap="56px"
        direction="column"
        alignItems="center"
        borderRadius="20px"
        padding="40px"
      >
        <Flex
          gap="40px"
          direction="column"
          alignItems="center"
          shrink="0"
        >
          {/* タイトル */}
          <CommonTitle
            shrink="0"
            alignSelf="stretch"
            position="relative"
            size="large"
            overrides={{
              text: {
                children: "推移データ作成",
              }
            }}
          ></CommonTitle>

          {/* モーダルの内容 */}
          <Flex
            gap="32px"
            direction="column"
            alignItems="center"
            shrink="0"
          >
            {/* 拠点選択 */}
            <DatakanriDataMakingLocation
              error="false"
              example="false"
              overrides={{
                SelectField: {
                  width: "405px",
                  value: selectedLocation,
                  placeholder: undefined,
                  children: (
                    <>
                      {
                        Object.keys(displaySettingJson.location_name).map((key, index) => {
                          return (
                            <option
                              key={key}
                              value={key}
                            >
                              {displaySettingJson.location_name[key]}
                            </option>
                          )
                        })
                      }
                    </>
                  ),
                  onChange: (e) => {
                    console.log(e.target.value);
                    setSelectedLocation(e.target.value);
                  }
                },
              }}
            />
            {/* スロット選択 */}
            <DatakanriDataMakingLocation
              error="false"
              example="false"
              overrides={{
                SelectField: {
                  label: "スロット",
                  width: "405px",
                  placeholder: undefined,
                  value: selectedSlot,
                  children: (
                    <>
                      {
                        Object.keys(displaySettingJson.band_label).map((key, index) => {
                          return (
                            <option
                              key={key}
                              value={key}
                            >
                              {displaySettingJson.band_label[key].name}
                            </option>
                          )
                        })
                      }
                    </>
                  ),
                  onChange: (e) => {
                    setSelectedSlot(e.target.value);
                  }
                },
              }}
            />

            {/* 期間選択 */}
            <Flex width="100%" direction="column">
              <Flex width="100%" alignItems="flex-end">
                <Flex grow={1}>
                  <TextField
                    label="期間"
                    type="month"
                    width="100%"
                    value={selectedPeriod.start}
                    min="2024-03"
                    onChange={(e) => {
                      setSelectedPeriod({
                        ...selectedPeriod,
                        start: e.target.value,
                      });
                    }}
                    onBlur={() => {validate();}}
                  />
                </Flex>
                <Flex alignItems="center" paddingBottom="0.7rem">~</Flex>
                <Flex grow={1}>
                  <TextField
                    type="month"
                    width="100%"
                    min="2024-03"
                    value={selectedPeriod.end}
                    onChange={(e) => {
                      setSelectedPeriod({
                        ...selectedPeriod,
                        end: e.target.value,
                      });
                    }}
                    onBlur={() => {validate();}}
                  />
                </Flex>
              </Flex>

              {/* エラーメッセージ */}
              {
                validPeriodMeg !== "" && (
                  <Flex>
                    <Myicons
                      display="block"
                      type="attention"
                      size="small"
                    />
                    <Text
                      color="rgba(255,76,108,1)"
                      fontSize="12px"
                    >
                      {validPeriodMeg}
                    </Text>
                  </Flex>
                )
              }

            </Flex>
          </Flex>
        </Flex>

        {/* 作成ボタン */}
        <CommonBtnBlue
          size="large"
          style={{
            cursor: "pointer",
          }}
          disabled={isLoading}
          overrides={{
            text: {
              children: "作成",
              onClick: onClickCreateBtn,
            }
          }}
        ></CommonBtnBlue>
      </Flex >
    </>
  );
}

export default CreateTrendGraph;